import { useCallback, useEffect, useState } from "react";
import Navbar from "../Navbar";
import TreatImage from "./TreatImage";
import AdminTreatReports from "./AdminTreatReports";
import {
  adminTreatTransactions,
  adminTreatItems,
  adminCreateTreatItem,
  adminUpdateTreatItem,
  adminPayoutAccounts,
  adminRevealAccountNumber,
  adminVerifyPayoutAccount,
  adminRejectPayoutAccount,
  adminBloggerPayouts,
  adminStartPayout,
  adminMarkPayoutPaid,
  adminMarkPayoutFailed,
} from "../../services/treatApi";
import { formatMoney } from "../../services/proSystemApi";
import "../Pro/Pro.css";
import "./Treat.css";

// =====================================================
// BUY ME A TREAT — ADMIN  (Steps 3, 9, 11, 12)  — /admin-treats
//
// Behind ProtectedAdminRoute here and adminOnly on the server; every number
// on these screens is computed by the server.
// =====================================================

const TABS = [
  { key: "transactions", label: "Treat Transactions" },
  { key: "payouts", label: "Blogger Payouts" },
  { key: "verification", label: "Payout Verification" },
  { key: "items", label: "Treats" },
  { key: "reports", label: "Reported Greetings" },
];

const fmt = (v) => (v ? new Date(v).toLocaleString("en-IN") : "—");
const errMsg = (err, fallback) => err?.response?.data?.message || fallback;

const PAY_PILL = { paid: "paid", created: "pending", failed: "failed" };
const PAY_LABEL = { paid: "Successful", created: "Pending", failed: "Failed" };
const PAYOUT_LABEL = { unpaid: "Unpaid", processing: "Processing", paid: "Paid" };

function useToast() {
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);
  return [toast, setToast];
}

// ── TREAT TRANSACTIONS ─────────────────────────────────
function TransactionsPanel() {
  const [filters, setFilters] = useState({ from: "", to: "", blogger: "", reader: "", treat: "", status: "" });
  const [applied, setApplied] = useState(filters);
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    adminTreatItems().then((r) => setItems(r.items)).catch(() => {});
  }, []);

  useEffect(() => {
    setData(null);
    adminTreatTransactions(applied)
      .then((r) => { setData(r); setError(null); })
      .catch((err) => setError(errMsg(err, "Failed to load transactions")));
  }, [applied]);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      <form className="treat-filters" onSubmit={(e) => { e.preventDefault(); setApplied(filters); }}>
        <label>From <input type="date" className="pro-input" value={filters.from} onChange={set("from")} /></label>
        <label>To <input type="date" className="pro-input" value={filters.to} onChange={set("to")} /></label>
        <label>Blogger <input className="pro-input" value={filters.blogger} onChange={set("blogger")} placeholder="name" /></label>
        <label>Reader <input className="pro-input" value={filters.reader} onChange={set("reader")} placeholder="name" /></label>
        <label>Treat
          <select className="pro-input" value={filters.treat} onChange={set("treat")}>
            <option value="">All</option>
            {items.map((i) => <option key={i._id} value={i._id}>{i.name}</option>)}
          </select>
        </label>
        <label>Payment
          <select className="pro-input" value={filters.status} onChange={set("status")}>
            <option value="">All</option>
            <option value="paid">Successful</option>
            <option value="created">Pending</option>
            <option value="failed">Failed</option>
          </select>
        </label>
        <button type="submit" className="pro-btn pro-btn-primary pro-btn-sm">Apply</button>
      </form>

      {error && <div className="pro-card pro-empty"><p>{error}</p></div>}
      {!data && !error && <div className="pro-loading-screen"><div className="pro-spinner" /></div>}

      {data && (
        <>
          <section className="pro-money-grid" aria-label="Totals">
            <div className="pro-money-card"><small>Successful payments</small><strong>{data.totals.paidCount}</strong></div>
            <div className="pro-money-card"><small>Total collected</small><strong>{formatMoney(data.totals.grossRupees)}</strong></div>
            <div className="pro-money-card pro-money-primary"><small>Commission ({data.commissionPercent}%)</small><strong>{formatMoney(data.totals.commissionRupees)}</strong></div>
            <div className="pro-money-card"><small>Blogger share (90%)</small><strong>{formatMoney(data.totals.bloggerShareRupees)}</strong></div>
          </section>

          {data.items.length === 0 ? (
            <div className="pro-card pro-empty"><p>No transactions match these filters.</p></div>
          ) : (
            <div className="pro-table-scroll">
              <table className="pro-table">
                <thead>
                  <tr>
                    <th>Transaction ID</th><th>Reader</th><th>Blogger</th><th>Blog</th><th>Treat</th>
                    <th>Total</th><th>Commission 10%</th><th>Blogger 90%</th><th>Date and time</th>
                    <th>Razorpay reference</th><th>Payment</th><th>Payout</th><th>Refund</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((t) => (
                    <tr key={t._id}>
                      <td className="treat-mono">{t.transactionId}</td>
                      <td>{t.reader?.name || "—"}</td>
                      <td>{t.blogger?.name || "—"}</td>
                      <td>{t.blogTitle || "—"}</td>
                      <td>{t.treatName}</td>
                      <td>{formatMoney(t.totalRupees)}</td>
                      <td>{t.commissionRupees == null ? "—" : formatMoney(t.commissionRupees)}</td>
                      <td>{t.bloggerShareRupees == null ? "—" : formatMoney(t.bloggerShareRupees)}</td>
                      <td>{fmt(t.date)}</td>
                      <td className="treat-mono">
                        {t.razorpayPaymentId || "—"}
                        <small className="pro-table-sub">{t.razorpayOrderId}</small>
                      </td>
                      <td>
                        <span className={`pro-status-pill pro-status-${PAY_PILL[t.paymentStatus]}`}>{PAY_LABEL[t.paymentStatus]}</span>
                        {t.failureReason && <small className="pro-table-sub">{t.failureReason}</small>}
                      </td>
                      <td>{t.payoutStatus ? PAYOUT_LABEL[t.payoutStatus] : "—"}</td>
                      <td>{t.refundStatus === "refunded" ? "Refunded" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ── BLOGGER PAYOUTS ────────────────────────────────────
function PayoutsPanel({ setToast }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminBloggerPayouts()
      .then((r) => { setData(r); setError(null); })
      .catch((err) => setError(errMsg(err, "Failed to load payouts")));
  }, []);
  useEffect(load, [load]);

  const run = async (fn) => {
    setBusy(true);
    try {
      const r = await fn();
      setToast({ type: "success", msg: r?.message || "Done." });
      load();
    } catch (err) {
      setToast({ type: "error", msg: errMsg(err, "Action failed") });
    } finally {
      setBusy(false);
    }
  };

  const start = (row) => {
    if (!window.confirm(`Start a payout of ${formatMoney(row.pendingPayoutRupees)} to ${row.blogger.name}?`)) return;
    run(() => adminStartPayout(row.blogger._id, ""));
  };

  const markPaid = (p) => {
    const reference = window.prompt("Transfer reference (UTR) of the completed transfer:", "");
    if (!reference) return;
    run(() => adminMarkPayoutPaid(p._id, reference.trim(), ""));
  };

  const markFailed = (p) => {
    const note = window.prompt("Why did the transfer fail?", "");
    if (note === null) return;
    run(() => adminMarkPayoutFailed(p._id, note));
  };

  if (error) return <div className="pro-card pro-empty"><p>{error}</p></div>;
  if (!data) return <div className="pro-loading-screen"><div className="pro-spinner" /></div>;
  if (data.items.length === 0) return <div className="pro-card pro-empty"><p>No blogger has received a treat yet.</p></div>;

  return (
    <div className="treat-card-list">
      {data.items.map((row) => (
        <article key={row.blogger._id} className="pro-card">
          <div className="treat-sent-head">
            <div className="treat-sent-main">
              <strong>{row.blogger.name}</strong>
              <span>{row.blogger.email}</span>
            </div>
            <span className={`pro-status-pill pro-status-${row.bankStatus === "verified" ? "paid" : row.bankStatus === "rejected" ? "failed" : "pending"}`}>
              Bank: {row.bankStatus === "none" ? "not added" : row.bankStatus}
            </span>
          </div>
          <dl className="treat-confirm-list">
            <div><dt>Total treat amount</dt><dd>{formatMoney(row.totalTreatRupees)}</dd></div>
            <div><dt>Total app commission</dt><dd>{formatMoney(row.commissionRupees)}</dd></div>
            <div><dt>Blogger payable</dt><dd>{formatMoney(row.payableRupees)}</dd></div>
            <div><dt>Already paid</dt><dd>{formatMoney(row.paidRupees)}</dd></div>
            <div><dt>In progress</dt><dd>{formatMoney(row.processingRupees)}</dd></div>
            <div><dt>Pending payout</dt><dd>{formatMoney(row.pendingPayoutRupees)}</dd></div>
          </dl>
          {row.bank && (
            <p className="treat-dim">
              Pay to: {row.bank.accountHolderName} · {row.bank.accountNumberMasked} · {row.bank.ifsc}
            </p>
          )}
          <div className="treat-actions">
            <button
              type="button"
              className="pro-btn pro-btn-primary pro-btn-sm"
              disabled={busy || row.pendingPayoutRupees <= 0 || row.bankStatus !== "verified"}
              onClick={() => start(row)}
              title={row.bankStatus !== "verified" ? "Verify the blogger's payout details first" : ""}
            >
              Start payout
            </button>
          </div>

          {row.history.length > 0 && (
            <div className="pro-table-scroll">
              <table className="pro-table">
                <thead><tr><th>Payout history</th><th>Amount</th><th>Status</th><th>Reference</th><th>Paid on</th><th /></tr></thead>
                <tbody>
                  {row.history.map((p) => (
                    <tr key={p._id}>
                      <td>{fmt(p.createdAt)}</td>
                      <td>{formatMoney(p.amountRupees)} <small className="pro-table-sub">{p.treatCount} treats</small></td>
                      <td><span className={`pro-status-pill pro-status-${p.status === "processing" ? "processing" : p.status}`}>{p.status}</span></td>
                      <td className="treat-mono">{p.reference || "—"}</td>
                      <td>{fmt(p.paidAt)}</td>
                      <td>
                        {p.status === "processing" && (
                          <div className="treat-admin-actions">
                            <button type="button" className="pro-btn pro-btn-sm pro-btn-primary" disabled={busy} onClick={() => markPaid(p)}>Mark Paid</button>
                            <button type="button" className="pro-btn pro-btn-sm pro-btn-ghost" disabled={busy} onClick={() => markFailed(p)}>Failed</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

// ── PAYOUT VERIFICATION ────────────────────────────────
function VerificationPanel({ setToast }) {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [revealed, setRevealed] = useState({});

  const load = useCallback(() => {
    setData(null);
    adminPayoutAccounts(status)
      .then((r) => { setData(r); setError(null); })
      .catch((err) => setError(errMsg(err, "Failed to load payout details")));
  }, [status]);
  useEffect(load, [load]);

  const reveal = async (id) => {
    if (revealed[id]) return setRevealed((r) => ({ ...r, [id]: null }));
    try {
      const r = await adminRevealAccountNumber(id);
      setRevealed((x) => ({ ...x, [id]: r.accountNumber }));
    } catch (err) {
      setToast({ type: "error", msg: errMsg(err, "Could not load the account number") });
    }
  };

  const act = async (id, kind) => {
    try {
      let r;
      if (kind === "verify") r = await adminVerifyPayoutAccount(id);
      else {
        const reason = window.prompt("Reason for rejection (shown to the blogger):", "");
        if (!reason) return;
        r = await adminRejectPayoutAccount(id, reason.trim());
      }
      setToast({ type: "success", msg: r?.message || "Done." });
      load();
    } catch (err) {
      setToast({ type: "error", msg: errMsg(err, "Action failed") });
    }
  };

  return (
    <>
      <div className="pro-tabs" role="tablist">
        {["pending", "verified", "rejected", ""].map((s) => (
          <button key={s || "all"} type="button" role="tab" aria-selected={status === s}
            className={`pro-tab${status === s ? " pro-tab-active" : ""}`} onClick={() => setStatus(s)}>
            {s ? s[0].toUpperCase() + s.slice(1) : "All"}
            {data && s && <span className="pro-tab-count">{data.counts[s]}</span>}
          </button>
        ))}
      </div>
      {error && <div className="pro-card pro-empty"><p>{error}</p></div>}
      {!data && !error && <div className="pro-loading-screen"><div className="pro-spinner" /></div>}
      {data && data.items.length === 0 && <div className="pro-card pro-empty"><p>Nothing here.</p></div>}
      {data && data.items.length > 0 && (
        <div className="pro-table-scroll">
          <table className="pro-table">
            <thead><tr><th>Blogger</th><th>Account holder</th><th>Account number</th><th>IFSC</th><th>Phone</th><th>Submitted</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {data.items.map((a) => (
                <tr key={a._id}>
                  <td>{a.blogger?.name || "—"}<small className="pro-table-sub">{a.blogger?.email}</small></td>
                  <td>{a.accountHolderName}</td>
                  <td className="treat-mono">
                    {revealed[a._id] || a.accountNumberMasked}
                    <button type="button" className="treat-report-link" onClick={() => reveal(a._id)}>
                      {revealed[a._id] ? "Hide" : "Show"}
                    </button>
                  </td>
                  <td className="treat-mono">{a.ifsc}</td>
                  <td>{a.phone}</td>
                  <td>{fmt(a.submittedAt)}</td>
                  <td>
                    <span className={`pro-status-pill pro-status-${a.status === "verified" ? "paid" : a.status === "rejected" ? "failed" : "pending"}`}>{a.status}</span>
                    {a.rejectionReason && <small className="pro-table-sub">{a.rejectionReason}</small>}
                  </td>
                  <td>
                    <div className="treat-admin-actions">
                      {a.status !== "verified" && <button type="button" className="pro-btn pro-btn-sm pro-btn-primary" onClick={() => act(a._id, "verify")}>Verify</button>}
                      {a.status !== "rejected" && <button type="button" className="pro-btn pro-btn-sm treat-btn-danger" onClick={() => act(a._id, "reject")}>Reject</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── TREAT CATALOGUE ────────────────────────────────────
function ItemsPanel({ setToast }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", priceRupees: "", image: null });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    adminTreatItems().then((r) => setItems(r.items)).catch((err) => setToast({ type: "error", msg: errMsg(err, "Failed to load treats") }));
  }, [setToast]);
  useEffect(load, [load]);

  const add = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("priceRupees", form.priceRupees);
    if (form.image) fd.append("image", form.image);
    setSaving(true);
    try {
      const r = await adminCreateTreatItem(fd);
      setToast({ type: "success", msg: r.message });
      setForm({ name: "", priceRupees: "", image: null });
      e.target.reset();
      load();
    } catch (err) {
      setToast({ type: "error", msg: errMsg(err, "Could not add the treat") });
    } finally {
      setSaving(false);
    }
  };

  const update = async (item, changes, file) => {
    const fd = new FormData();
    Object.entries(changes).forEach(([k, v]) => fd.append(k, String(v)));
    if (file) fd.append("image", file);
    try {
      const r = await adminUpdateTreatItem(item._id, fd);
      setToast({ type: "success", msg: r.message });
      load();
    } catch (err) {
      setToast({ type: "error", msg: errMsg(err, "Could not update the treat") });
    }
  };

  const changePrice = (item) => {
    const v = window.prompt(`New price for ${item.name} (Rs.)`, String(item.priceRupees));
    if (v === null || v.trim() === "") return;
    update(item, { priceRupees: v.trim() });
  };

  return (
    <>
      <form className="pro-card treat-filters" onSubmit={add}>
        <label>Name <input className="pro-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={40} required /></label>
        <label>Price (Rs.) <input className="pro-input" type="number" min="1" step="0.01" value={form.priceRupees} onChange={(e) => setForm({ ...form, priceRupees: e.target.value })} required /></label>
        <label>Image <input type="file" accept="image/*" onChange={(e) => setForm({ ...form, image: e.target.files?.[0] || null })} required /></label>
        <button type="submit" className="pro-btn pro-btn-primary pro-btn-sm" disabled={saving}>{saving ? "Adding…" : "Add treat"}</button>
      </form>

      {!items && <div className="pro-loading-screen"><div className="pro-spinner" /></div>}
      {items && (
        <div className="pro-table-scroll">
          <table className="pro-table">
            <thead><tr><th>Treat</th><th>Price</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {items.map((i) => (
                <tr key={i._id}>
                  <td><span className="treat-cell-treat"><TreatImage image={i.image} name={i.name} size={36} />{i.name}</span></td>
                  <td>Rs. {i.priceRupees}</td>
                  <td><span className={`pro-status-pill pro-status-${i.active ? "active" : "expired"}`}>{i.active ? "Active" : "Inactive"}</span></td>
                  <td>
                    <div className="treat-admin-actions">
                      <button type="button" className="pro-btn pro-btn-sm pro-btn-ghost" onClick={() => changePrice(i)}>Change price</button>
                      <button type="button" className="pro-btn pro-btn-sm pro-btn-ghost" onClick={() => update(i, { active: !i.active })}>
                        {i.active ? "Deactivate" : "Activate"}
                      </button>
                      <label className="pro-btn pro-btn-sm pro-btn-ghost treat-file-btn">
                        Change image
                        <input type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && update(i, {}, e.target.files[0])} />
                      </label>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="treat-dim">A price change applies to new orders only; orders already opened keep their price.</p>
    </>
  );
}

function AdminTreats() {
  const [tab, setTab] = useState("transactions");
  const [toast, setToast] = useToast();

  return (
    <div className="pro-page-wrap">
      <Navbar />
      {toast && <div className={`pro-toast pro-toast-${toast.type}`}>{toast.msg}</div>}
      <div className="pro-container">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">🎁 BUY ME A TREAT — ADMIN</div>
          <h1 className="pro-hero-title">Treats, payouts and reports</h1>
        </header>

        <div className="pro-tabs pro-tabs-scroll" role="tablist">
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
              className={`pro-tab${tab === t.key ? " pro-tab-active" : ""}`} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "transactions" && <TransactionsPanel />}
        {tab === "payouts" && <PayoutsPanel setToast={setToast} />}
        {tab === "verification" && <VerificationPanel setToast={setToast} />}
        {tab === "items" && <ItemsPanel setToast={setToast} />}
        {tab === "reports" && <AdminTreatReports embedded />}
      </div>
    </div>
  );
}

export default AdminTreats;
